import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { fmtMoney, fmtTime12, shiftStyle, MONTHS } from "@/lib/format";
import {
  UsersThree, CalendarCheck, Wallet, TrendUp, Warning, HandCoins, Plant,
} from "@phosphor-icons/react";

const TONES = {
  slate: "bg-slate-100 text-slate-600",
  sky: "bg-sky-50 text-sky-600",
  amber: "bg-amber-50 text-amber-600",
  emerald: "bg-emerald-50 text-emerald-600",
  rose: "bg-rose-50 text-rose-600",
  teal: "bg-teal-50 text-teal-600",
};

const MetricCard = ({ label, value, icon: Icon, tone = "slate", testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 flex flex-col gap-3"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">{label}</span>
      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${TONES[tone]}`}>
        <Icon size={18} weight="bold" />
      </div>
    </div>
    <div className="font-data text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
      {value}
    </div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/shifts").then((r) => setShifts(r.data)).catch(() => {});
  }, []);

  const mLabel = data ? MONTHS[parseInt(data.month.slice(5, 7)) - 1] : "";

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto w-full space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle={data ? `Overview for ${mLabel} ${data.month.slice(0, 4)}` : "Loading..."}
      />

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <MetricCard testid="stat-labourers" label="Labourers" value={data.total_labourers} icon={UsersThree} tone="slate" />
            <MetricCard testid="stat-present" label="Present Today" value={data.present_today} icon={CalendarCheck} tone="sky" />
            <MetricCard testid="stat-earned" label="Earned (Month)" value={fmtMoney(data.month_earned)} icon={TrendUp} tone="amber" />
            <MetricCard testid="stat-lend" label="Lent (Month)" value={fmtMoney(data.month_lend)} icon={HandCoins} tone="rose" />
            <MetricCard testid="stat-paid" label="Paid (Month)" value={fmtMoney(data.month_paid)} icon={Wallet} tone="emerald" />
            <MetricCard testid="stat-outstanding" label="Net Balance" value={fmtMoney(data.outstanding_balance)} icon={Warning} tone="slate" />
          </div>

          {(data.arecanut_month_earned !== 0 || data.arecanut_balance !== 0 || data.arecanut_month_advance !== 0 || data.arecanut_month_lend !== 0) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <MetricCard testid="stat-arecanut-earned" label="Arecanut Earned (Month)" value={fmtMoney(data.arecanut_month_earned)} icon={Plant} tone="teal" />
              <MetricCard testid="stat-arecanut-advance" label="Arecanut Advance (Month)" value={fmtMoney(data.arecanut_month_advance)} icon={HandCoins} tone="teal" />
              <MetricCard testid="stat-arecanut-lend" label="Arecanut Lent (Month)" value={fmtMoney(data.arecanut_month_lend)} icon={HandCoins} tone="teal" />
              <MetricCard testid="stat-arecanut-balance" label="Arecanut Balance" value={fmtMoney(data.arecanut_balance)} icon={Warning} tone="teal" />
            </div>
          )}

          {shifts.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-medium text-slate-800 mb-3">Shift Timings</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {shifts.map((s, i) => (
                  <div key={s.id} className={`rounded-md border p-4 ${shiftStyle(i)}`}>
                    <div className="font-semibold">{s.name}</div>
                    <div className="font-data text-sm mt-1 opacity-80">
                      {fmtTime12(s.start)} – {fmtTime12(s.end)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.total_labourers === 0 && (
            <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center">
              <p className="text-slate-600 font-medium">No labourers yet.</p>
              <p className="text-sm text-slate-500 mt-1">Add labourers from the "Labourers" tab to start marking attendance.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
